import { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { Modal } from "../Modal";
import type { Student } from "../../services/studentService";
import { studentInitials } from "./studentDisplay";

type Props = {
  student: Student;
  isDeleting: boolean;
  error: unknown;
  onConfirm: () => void;
  onCancel: () => void;
  /** Offered as the safer path for a learner who is leaving. */
  onChangeStatus?: () => void;
};

/** Why the server refused, e.g. a learner who already has fees on record. */
export function deleteStudentError(error: unknown) {
  if (!error) return "";
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  return typeof detail === "string" && detail
    ? detail
    : "The student could not be deleted. Please try again.";
}

export default function DeleteStudentDialog({
  student,
  isDeleting,
  error,
  onConfirm,
  onCancel,
  onChangeStatus,
}: Props) {
  const [typed, setTyped] = useState("");
  const confirmed = typed.trim().toUpperCase() === student.student_id.toUpperCase();
  const message = deleteStudentError(error);

  return (
    <Modal
      isOpen
      size="md"
      title="Delete student record"
      onClose={() => {
        if (!isDeleting) onCancel();
      }}
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className="btn btn-outline"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="delete-student-form"
            disabled={!confirmed || isDeleting}
            className="btn gap-2 bg-red-600 text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
            {isDeleting ? "Deleting…" : "Delete permanently"}
          </button>
        </>
      }
    >
      <form
        id="delete-student-form"
        className="font-refined space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          if (confirmed && !isDeleting) onConfirm();
        }}
      >
        <div className="flex gap-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100">
            <Trash2 className="h-5 w-5 text-red-600" />
          </span>
          <div>
            <h4 className="text-base font-semibold tracking-tight text-gray-900">
              Delete {student.full_name}?
            </h4>
            <p className="mt-1 text-sm leading-relaxed text-gray-600">
              This permanently removes the student record and their portal
              login. <span className="font-medium text-gray-900">It cannot be undone.</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">
            {studentInitials(student)}
          </span>
          <div className="min-w-0 text-sm">
            <p className="truncate font-medium text-gray-900">{student.full_name}</p>
            <p className="truncate text-xs tabular-nums text-gray-500">
              <span className="font-mono">{student.student_id}</span>
              {student.current_class_name && ` · ${student.current_class_name}`}
              {" · login "}
              <span className="font-mono">{student.user.username}</span>
            </p>
          </div>
        </div>

        {onChangeStatus && (
          <p className="text-sm leading-relaxed text-gray-600">
            Is the learner leaving the school? Mark them withdrawn or
            transferred instead, which keeps their history.{" "}
            <button
              type="button"
              onClick={onChangeStatus}
              className="inline-flex items-center gap-1 font-medium text-primary-700 hover:underline"
            >
              <Pencil className="h-3.5 w-3.5" /> Change status instead
            </button>
          </p>
        )}

        <label className="block text-sm text-gray-700">
          Type <span className="font-mono font-semibold text-gray-900">{student.student_id}</span>{" "}
          to confirm
          <input
            autoFocus
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            className="input mt-1.5 font-mono tracking-wide"
            placeholder={student.student_id}
            aria-describedby={message ? "delete-student-error" : undefined}
          />
        </label>

        {message && (
          <p
            id="delete-student-error"
            role="alert"
            className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm leading-relaxed text-red-800"
          >
            {message}
          </p>
        )}
      </form>
    </Modal>
  );
}
